// src/components/LeaveApplication.jsx
import React, { useState, useEffect, useRef } from "react";
import { api } from "../api";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import "./LeaveApplication.css";

const officeStart = "10:00"; // 10 AM
const officeEnd = "19:00"; // 7 PM

// Financial year runs April -> March
const MONTH_ORDER = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2]; // Apr..Mar (dayjs month index)

const fyStartYear = (date) => {
  const d = dayjs(date);
  return d.month() >= 3 ? d.year() : d.year() - 1;
};
const fyLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(2)}`;

// number of leave days (Half Day = 0.5)
const leaveDays = (leave) => {
  if (leave.duration === "Half Day") return 0.5;
  const from = dayjs(leave.fromDate);
  const to = dayjs(leave.toDate);
  if (!from.isValid() || !to.isValid()) return 0;
  return Math.max(to.startOf("day").diff(from.startOf("day"), "day") + 1, 1);
};

const fmtDays = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

const statusClass = (status) =>
  status === "Approved"
    ? "la-status la-status--approved"
    : status === "Rejected"
      ? "la-status la-status--rejected"
      : "la-status la-status--pending";

// Unique id per submit, so the server can ignore a repeated request.
const makeClientId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const LeaveApplication = () => {
  const [form, setForm] = useState({
    type: "Casual Leave",
    duration: "Full Day",
    fromDate: "",
    toDate: "",
    reason: "",
    halfDayOutTime: "", // leaving time
    halfDayInTime: "", // returning time
  });

  const [leaveRequests, setLeaveRequests] = useState([]);

  // filters: FY start year as string ("" = auto, "all" = all years), month index as string ("all" = all months)
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const submitLock = useRef(false); // blocks double click instantly

  const today = dayjs().format("YYYY-MM-DD");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Half Day: keep dates same day
    if (form.duration === "Half Day") {
      if (name === "fromDate") {
        setForm((f) => ({ ...f, fromDate: value, toDate: value || f.toDate }));
        return;
      }
      if (name === "toDate") {
        setForm((f) => ({ ...f, fromDate: value, toDate: value }));
        return;
      }
    }

    setField(name, value);
  };

  const fetchLeaves = async () => {
    try {
      const res = await api.get("/leaves/my", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setLeaveRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load leaves:", err);
    } finally {
      setIsLoadingList(false);
    }
  };

  const validate = () => {
    const errs = [];
    if (!form.type) errs.push("Leave Type");
    if (!form.duration) errs.push("Duration");
    if (!form.fromDate) errs.push("From Date");
    if (!form.toDate) errs.push("To Date");
    if (!form.reason.trim()) errs.push("Reason");

    // date logic
    const from = dayjs(form.fromDate);
    const to = dayjs(form.toDate);
    if (from.isAfter(to)) errs.push("From Date must be on/before To Date");

    if (form.duration === "Half Day") {
      if (!form.fromDate || !form.toDate || !from.isSame(to, "day")) {
        errs.push("Half Day leave must be for a single day");
      }

      // at least one of out/in
      if (!form.halfDayOutTime && !form.halfDayInTime) {
        errs.push("Provide Leaving or Returning time for Half Day");
      }

      // if both present, returning must be after leaving
      if (form.halfDayOutTime && form.halfDayInTime) {
        const outM = toMinutes(form.halfDayOutTime);
        const inM = toMinutes(form.halfDayInTime);
        if (inM <= outM) errs.push("Returning time must be after Leaving time");
      }
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // already submitting -> ignore extra clicks / Enter presses
    if (submitLock.current) return;

    const errs = validate();
    if (errs.length) {
      alert("❌ Please fix:\n- " + errs.join("\n- "));
      return;
    }

    submitLock.current = true;
    setIsSubmitting(true);

    const payload = {
      type: form.type,
      duration: form.duration,
      fromDate: form.fromDate,
      toDate: form.toDate,
      reason: form.reason.trim(),
      halfDayOutTime: form.halfDayOutTime || null,
      halfDayInTime: form.halfDayInTime || null,
      clientId: makeClientId(),
    };

    try {
      await api.post("/leaves/apply", payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      // jump filters to the new leave, so it is visible right away
      setSelectedFY(String(fyStartYear(payload.fromDate)));
      setSelectedMonth(String(dayjs(payload.fromDate).month()));

      setForm({
        type: "Casual Leave",
        duration: "Full Day",
        fromDate: "",
        toDate: "",
        reason: "",
        halfDayOutTime: "",
        halfDayInTime: "",
      });

      // wait for the fresh list before telling the user
      await fetchLeaves();

      alert("✅ Leave Application Submitted!");
    } catch (err) {
      console.error("Leave apply failed:", err);
      alert(
        "❌ " +
          (err?.response?.data?.message ||
            "Could not submit leave. Please try again."),
      );
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  // ---------- helpers ----------
  const toMinutes = (hhmm) => {
    const [h, m] = String(hhmm || "0:0")
      .split(":")
      .map(Number);
    return h * 60 + m;
  };

  const fmtTime = (hhmm) =>
    hhmm ? dayjs(`2000-01-01T${hhmm}`).format("hh:mm A") : "-"; // only time; no 1970

  const calculateHours = (duration, outTime, inTime) => {
    if (duration !== "Half Day") return "9 hrs";

    const startDay = toMinutes(officeStart);
    const endDay = toMinutes(officeEnd);

    // both provided → diff
    if (outTime && inTime) {
      const diff = Math.max(toMinutes(inTime) - toMinutes(outTime), 0);
      return (diff / 60).toFixed(1) + " hrs";
    }
    // only out → till office end
    if (outTime) {
      const diff = Math.max(endDay - toMinutes(outTime), 0);
      return (diff / 60).toFixed(1) + " hrs";
    }
    // only in → from office start
    if (inTime) {
      const diff = Math.max(toMinutes(inTime) - startDay, 0);
      return (diff / 60).toFixed(1) + " hrs";
    }
    return "0 hrs";
  };

  // ---------- valid leaves, newest first ----------
  const sortedLeaves = leaveRequests
    .filter((l) => dayjs(l.fromDate).isValid())
    .sort((a, b) => dayjs(b.fromDate).valueOf() - dayjs(a.fromDate).valueOf());

  // ---------- financial-year summary (all years) ----------
  const fySummary = {};
  sortedLeaves.forEach((l) => {
    const fy = fyStartYear(l.fromDate);
    if (!fySummary[fy]) {
      fySummary[fy] = { approved: 0, pending: 0, rejected: 0, byType: {} };
    }
    const days = leaveDays(l);
    if (l.status === "Approved") {
      fySummary[fy].approved += days;
      fySummary[fy].byType[l.type] = (fySummary[fy].byType[l.type] || 0) + days;
    } else if (l.status === "Rejected") {
      fySummary[fy].rejected += days;
    } else {
      fySummary[fy].pending += days;
    }
  });

  // only financial years that have entries (newest first)
  const availableFYs = Object.keys(fySummary)
    .map(Number)
    .sort((a, b) => b - a);

  // default: current FY if it has entries, else newest FY
  const currentFY = fyStartYear(dayjs());
  const defaultFY = availableFYs.includes(currentFY)
    ? String(currentFY)
    : availableFYs[0] !== undefined
      ? String(availableFYs[0])
      : "all";

  const activeFY =
    selectedFY === "all" || availableFYs.includes(Number(selectedFY))
      ? selectedFY || defaultFY
      : defaultFY;

  const leavesInFY =
    activeFY === "all"
      ? sortedLeaves
      : sortedLeaves.filter(
          (l) => fyStartYear(l.fromDate) === Number(activeFY),
        );

  // only months that have entries, in April -> March order
  const monthsWithData = MONTH_ORDER.filter((m) =>
    leavesInFY.some((l) => dayjs(l.fromDate).month() === m),
  );

  const activeMonth =
    selectedMonth !== "all" && monthsWithData.includes(Number(selectedMonth))
      ? selectedMonth
      : "all";

  const filteredLeaves =
    activeMonth === "all"
      ? leavesInFY
      : leavesInFY.filter(
          (l) => dayjs(l.fromDate).month() === Number(activeMonth),
        );

  const monthName = (m) => dayjs().month(m).format("MMMM");

  const activeFYLabel =
    activeFY === "all" ? "All Years" : `FY ${fyLabel(Number(activeFY))}`;
  const heading =
    activeMonth === "all"
      ? activeFYLabel
      : `${monthName(Number(activeMonth))} • ${activeFYLabel}`;

  const typeBreakdown =
    activeFY !== "all" && fySummary[activeFY]
      ? Object.entries(fySummary[activeFY].byType)
      : [];

  return (
    <div className="la">
      {/* ===== Apply Card ===== */}
      <div className="la-card">
        <h2 className="la-heading">📅 Leave Application</h2>

        <form onSubmit={handleSubmit} className="la-form">
          <TextField
            select
            label="Leave Type"
            name="type"
            value={form.type}
            onChange={handleChange}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
          >
            <option>Casual Leave</option>
            <option>Sick Leave</option>
            <option>Emergency Leave</option>
          </TextField>

          <TextField
            select
            label="Duration"
            name="duration"
            value={form.duration}
            onChange={(e) => {
              // reset half-day times when switching
              const v = e.target.value;
              setForm((f) => ({
                ...f,
                duration: v,
                ...(v === "Half Day"
                  ? {
                      toDate: f.fromDate || "",
                      halfDayOutTime: "",
                      halfDayInTime: "",
                    }
                  : { halfDayOutTime: "", halfDayInTime: "" }),
              }));
            }}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
          >
            <option>Full Day</option>
            <option>Half Day</option>
          </TextField>

          {form.duration === "Half Day" && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="la-time-grid">
                <TimePicker
                  ampm
                  label="Leaving Time"
                  value={
                    form.halfDayOutTime
                      ? dayjs(`2000-01-01T${form.halfDayOutTime}`)
                      : null
                  }
                  onChange={(val) =>
                    setField(
                      "halfDayOutTime",
                      val ? dayjs(val).format("HH:mm") : "",
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
                <TimePicker
                  ampm
                  label="Returning Time"
                  value={
                    form.halfDayInTime
                      ? dayjs(`2000-01-01T${form.halfDayInTime}`)
                      : null
                  }
                  onChange={(val) =>
                    setField(
                      "halfDayInTime",
                      val ? dayjs(val).format("HH:mm") : "",
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </div>
            </LocalizationProvider>
          )}

          <TextField
            type="date"
            label="From Date"
            name="fromDate"
            value={form.fromDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            fullWidth
            size="small"
          />

          <TextField
            type="date"
            label="To Date"
            name="toDate"
            value={form.toDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: form.fromDate || today,
            }}
            fullWidth
            size="small"
          />

          <TextField
            label="Reason"
            name="reason"
            value={form.reason}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
            size="small"
          />

          <button type="submit" disabled={isSubmitting} className="la-btn">
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>

      {/* ===== List Card ===== */}
      <div className="la-card la-card--list">
        <h3 className="la-heading">📄 My Leave Requests</h3>

        {/* ----- Financial year-wise leave count (April to March) ----- */}
        {availableFYs.length > 0 && (
          <>
            <h4 className="la-summary-title">
              Leave taken (Financial Year, April to March)
            </h4>
            <div className="la-summary">
              {availableFYs.map((fy) => {
                const s = fySummary[fy];
                return (
                  <button
                    type="button"
                    key={fy}
                    className={`la-fy-card ${
                      String(fy) === activeFY ? "is-active" : ""
                    }`}
                    onClick={() => {
                      setSelectedFY(String(fy));
                      setSelectedMonth("all");
                    }}
                  >
                    <div className="la-fy-name">FY {fyLabel(fy)}</div>
                    <div className="la-fy-taken">
                      {fmtDays(s.approved)}
                      <small>days taken</small>
                    </div>
                    <div className="la-fy-meta">
                      <span className="la-fy-pending">
                        Pending {fmtDays(s.pending)}
                      </span>
                      <span className="la-fy-rejected">
                        Rejected {fmtDays(s.rejected)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* ----- Filters: Year + Month (only those with entries) ----- */}
        <div className="la-filters">
          <div className="la-filter">
            <label htmlFor="la-year">Year:</label>
            <select
              id="la-year"
              value={activeFY}
              onChange={(e) => {
                setSelectedFY(e.target.value);
                setSelectedMonth("all");
              }}
            >
              <option value="all">All Years</option>
              {availableFYs.map((fy) => (
                <option key={fy} value={String(fy)}>
                  FY {fyLabel(fy)}
                </option>
              ))}
            </select>
          </div>

          <div className="la-filter">
            <label htmlFor="la-month">Month:</label>
            <select
              id="la-month"
              value={activeMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="all">All Months</option>
              {monthsWithData.map((m) => (
                <option key={m} value={String(m)}>
                  {monthName(m)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ----- Type-wise taken days for selected FY ----- */}
        {typeBreakdown.length > 0 && (
          <div className="la-types">
            {typeBreakdown.map(([type, days]) => (
              <span key={type} className="la-type-chip">
                {type}: {fmtDays(days)} days
              </span>
            ))}
          </div>
        )}

        <h4 className="la-list-heading">{heading}</h4>

        <table className="la-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>From</th>
              <th>To</th>
              <th>Duration</th>
              <th>Hours</th>
              <th>Leaving</th>
              <th>Returning</th>
              <th>Status</th>
              <th>Admin Remark</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingList ? (
              <tr>
                <td colSpan="9">Loading leave records...</td>
              </tr>
            ) : filteredLeaves.length === 0 ? (
              <tr>
                <td colSpan="9">No leave records found</td>
              </tr>
            ) : (
              filteredLeaves.map((leave, i) => (
                <tr key={leave._id || i}>
                  <td>{leave.type}</td>
                  <td>
                    {leave.fromDate
                      ? dayjs(leave.fromDate).format("DD/MM/YYYY")
                      : "-"}
                  </td>
                  <td>
                    {leave.toDate
                      ? dayjs(leave.toDate).format("DD/MM/YYYY")
                      : "-"}
                  </td>
                  <td>{leave.duration}</td>
                  <td>
                    {calculateHours(
                      leave.duration,
                      leave.halfDayOutTime,
                      leave.halfDayInTime,
                    )}
                  </td>
                  <td>{fmtTime(leave.halfDayOutTime)}</td>
                  <td>{fmtTime(leave.halfDayInTime)}</td>
                  <td className={statusClass(leave.status)}>{leave.status}</td>
                  <td>
                    {leave.remark ? (
                      <span className="la-remark">📝 {leave.remark}</span>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaveApplication;
